import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Avatar from '@mui/material/Avatar'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import LogoutIcon from '@mui/icons-material/Logout'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'

import Adam from '../images/login/Adam_login.png'
import Ash from '../images/login/Ash_login.png'
import Lucy from '../images/login/Lucy_login.png'
import Nancy from '../images/login/Nancy_login.png'
import { useAppSelector, useAppDispatch } from '../hooks'
import { setLoggedIn, setUserData } from '../stores/UserStore'
import { getAvatarString, getColorByString } from '../util'
import back4appService from '../services/back4app'

import { getPhaserGameSync } from '../PhaserGame'
import type Game from '../scenes/Game'
import BoringAvatar from 'boring-avatars'

const Wrapper = styled.form`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #222639;
  border-radius: 16px;
  padding: 36px 60px;
  box-shadow: 0px 0px 5px #0000006f;
`

const Title = styled.p`
  margin: 5px;
  font-size: 20px;
  color: #c2c2c2;
  text-align: center;
`

const RoomName = styled.div`
  max-width: 500px;
  max-height: 120px;
  overflow-wrap: anywhere;
  overflow-y: auto;
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;

  h3 {
    font-size: 24px;
    color: #eee;
  }
`

const RoomDescription = styled.div`
  max-width: 500px;
  max-height: 150px;
  overflow-wrap: anywhere;
  overflow-y: auto;
  font-size: 16px;
  color: #c2c2c2;
  display: flex;
  justify-content: center;
`

const SubTitle = styled.h3`
  width: 160px;
  font-size: 16px;
  color: #eee;
  text-align: center;
`

const Content = styled.div`
  display: flex;
  margin: 36px 0;
`

const Left = styled.div`
  margin-right: 48px;

  --swiper-navigation-size: 24px;

  .swiper {
    width: 160px;
    height: 220px;
    border-radius: 8px;
    overflow: hidden;
  }

  .swiper-slide {
    width: 160px;
    height: 220px;
    background: #dbdbe0;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .swiper-slide img {
    display: block;
    width: 95px;
    height: 136px;
    object-fit: contain;
  }
`

const Right = styled.div`
  width: 300px;
`

const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`

const Warning = styled.div`
  margin-top: 30px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const LogoutButton = styled(IconButton)`
  position: absolute;
  top: 10px;
  right: 10px;
`

const avatars = [
  { name: 'adam', img: Adam },
  { name: 'ash', img: Ash },
  { name: 'lucy', img: Lucy },
  { name: 'nancy', img: Nancy },
]

// shuffle the avatars array
for (let i = avatars.length - 1; i > 0; i--) {
  const j = Math.floor(Math.random() * (i + 1))
  ;[avatars[i], avatars[j]] = [avatars[j], avatars[i]]
}

export default function LoginDialog() {
  const [avatarIndex, setAvatarIndex] = useState<number>(0)
  const dispatch = useAppDispatch()
  const roomJoined = useAppSelector((state) => state.room.roomJoined)
  const roomName = useAppSelector((state) => state.room.roomName)
  const roomDescription = useAppSelector((state) => state.room.roomDescription)
  const userData = useAppSelector((state) => state.user.userData)

  // Set the avatar index based on the user's saved avatar
  useEffect(() => {
    if (userData && userData.avatar) {
      // User has an avatar, set the avatar index
      const savedAvatarIndex = avatars.findIndex((avatar) => avatar.name === userData.avatar)
      if (savedAvatarIndex !== -1) {
        setAvatarIndex(savedAvatarIndex)
      }
    }
  }, [userData])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (userData && roomJoined) {
      const phaserGame = getPhaserGameSync()
      const game = phaserGame?.scene?.keys?.game as Game | undefined
      if (!game) {
        console.error('[LoginDialog] Game not initialized')
        return
      }
      
      //console.log('Join! Name:', userData.username, 'Avatar:', avatars[avatarIndex].name)
      game.registerKeys()

      // Set player name using firstName and lastName if available
      let displayName = userData.username
      if (userData.firstName || userData.lastName) {
        const firstName = userData.firstName || ''
        const lastName = userData.lastName || ''
        displayName = `${firstName} ${lastName}`.trim()
      }

      game.myPlayer.setPlayerName(displayName)
      game.myPlayer.setPlayerRole(userData.role)

      // Use gameCharacter if available, or fall back to the selected avatar
      const gameCharacter = userData.gameCharacter || avatars[avatarIndex].name
      game.myPlayer.setPlayerTexture(gameCharacter)

      game.network.readyToConnect()
      dispatch(setLoggedIn(true))

      // Save the selected avatar to the user's profile if it changed
      if (userData.avatar !== avatars[avatarIndex].name) {
        // Profile column names: these keys are written to Parse as-is.
        const updates: { avatar: string; gameCharacter?: string } = {
          avatar: avatars[avatarIndex].name,
        }

        // Only set the game character if it doesn't exist yet
        if (!userData.gameCharacter) {
          updates.gameCharacter = avatars[avatarIndex].name
        }

        back4appService.profileService.updateProfile(userData.id, updates).catch((error) => {
          //console.error('Error updating avatar:', error)
        })
      }
    }
  }

  const handleLogout = async () => {
    await back4appService.authService.signOut()
    dispatch(setUserData(null))
    window.location.reload() // Reload to reset the application state
  }

  return (
    <Wrapper onSubmit={handleSubmit}>
      <LogoutButton color="secondary" onClick={handleLogout}>
        <Tooltip title="Logout">
          <LogoutIcon />
        </Tooltip>
      </LogoutButton>
      <Title>Joining</Title>
      <RoomName>
        <BoringAvatar name={roomName} size={40} variant="marble" />
        <h3>{roomName}</h3>
      </RoomName>
      <RoomDescription>
        <ArrowRightIcon /> {roomDescription}
      </RoomDescription>
      <Content>
        <Left>
          <SubTitle>Select an avatar</SubTitle>
          <Swiper
            modules={[Navigation]}
            navigation
            spaceBetween={0}
            slidesPerView={1}
            onSlideChange={(swiper) => {
              setAvatarIndex(swiper.activeIndex)
            }}
          >
            {avatars.map((avatar) => (
              <SwiperSlide key={avatar.name}>
                <img src={avatar.img} alt={avatar.name} loading="lazy" />
              </SwiperSlide>
            ))}
          </Swiper>
        </Left>
        <Right>
          <TextField
            fullWidth
            label="Username"
            variant="outlined"
            color="secondary"
            value={userData?.username || ''}
            disabled
          />
        </Right>
      </Content>
      <Bottom>
        <Button variant="contained" color="secondary" size="large" type="submit">
          Join
        </Button>
      </Bottom>
    </Wrapper>
  )
}
